import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { AlertTriangle, Plus, Trash2, Gavel, ShieldAlert, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LEVEL_BADGE = {
  verbal: 'bg-yellow-100 text-yellow-700', written: 'bg-orange-100 text-orange-700',
  final_written: 'bg-red-100 text-red-700', suspension: 'bg-purple-100 text-purple-700',
  termination: 'bg-gray-800 text-white',
};

const SEVERITY_OPTIONS = [
  { value: 'verbal', label: 'Verbal', color: 'bg-yellow-500' },
  { value: 'written', label: 'Written', color: 'bg-orange-500' },
  { value: 'final', label: 'Final', color: 'bg-red-500' },
];

const WarningLettersSection = ({ formData, setFormData, newWarning, setNewWarning, employee }) => {
  const [discipline, setDiscipline] = useState(null);
  useEffect(() => {
    if (!employee?.id) return;
    axios.get(`${API}/api/discipline/summary/${employee.id}`).then(r => setDiscipline(r.data)).catch(() => {});
  }, [employee?.id]);

  const addWarningLetter = () => {
    if (newWarning.issue_date && newWarning.reason) {
      const warningWithId = {
        ...newWarning,
        id: Date.now().toString(),
        severity: newWarning.severity || 'written',
        issued_by: newWarning.issued_by || '',
      };
      setFormData(prev => ({
        ...prev,
        warning_letters: [...prev.warning_letters, warningWithId]
      }));
      setNewWarning({ issue_date: '', reason: '', severity: 'written', issued_by: '' });
    }
  };

  const removeWarningLetter = (index) => {
    setFormData(prev => ({
      ...prev,
      warning_letters: prev.warning_letters.filter((_, i) => i !== index)
    }));
  };

  const getSeverityBadge = (severity) => {
    const opt = SEVERITY_OPTIONS.find(s => s.value === severity);
    return <Badge className={`${opt?.color || 'bg-gray-500'} text-[10px]`}>{opt?.label || severity}</Badge>;
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-yellow-600" /> Warning Letters
      </h3>

      {/* Discipline summary (links into the Discipline tracker) */}
      {employee?.id && discipline && discipline.total > 0 && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="profile-discipline-summary">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Gavel className="h-4 w-4 text-rose-600" />
              <span className="font-semibold text-gray-800">{discipline.active} active</span>
              <span className="text-gray-400">/ {discipline.total} total disciplinary record(s)</span>
              {discipline.latest_label && (
                <Badge className={`${LEVEL_BADGE[discipline.latest_level] || 'bg-gray-200'} text-[10px] ml-1`}>
                  Latest: {discipline.latest_label}
                </Badge>
              )}
            </div>
            <Link
              to={`/discipline?search=${encodeURIComponent(employee.employee_code || employee.name || '')}`}
              className="text-xs font-medium text-violet-600 hover:underline flex items-center gap-1"
              data-testid="profile-discipline-link"
            >
              Open in Discipline <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {discipline.escalation_hint && (
            <p className="mt-2 text-xs text-amber-800 flex items-center gap-1.5">
              <ShieldAlert className="h-3.5 w-3.5" /> {discipline.escalation_hint.message}
            </p>
          )}
        </div>
      )}
      
      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
        <Label className="mb-3 block">Add Warning Letter (Valid for 6 months)</Label>
        
        <div className="grid grid-cols-2 gap-2 mb-2">
          <Input 
            type="date"
            value={newWarning.issue_date}
            onChange={(e) => setNewWarning(prev => ({ ...prev, issue_date: e.target.value }))}
            data-testid="warning-date-input"
          />
          <Select 
            value={newWarning.severity || 'written'} 
            onValueChange={(v) => setNewWarning(prev => ({ ...prev, severity: v }))}
          >
            <SelectTrigger data-testid="warning-severity-select">
              <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent>
              {SEVERITY_OPTIONS.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 mb-3 flex-wrap">
          <Input 
            placeholder="Issued by"
            className="w-[180px]"
            value={newWarning.issued_by || ''}
            onChange={(e) => setNewWarning(prev => ({ ...prev, issued_by: e.target.value }))}
            data-testid="warning-issued-by-input"
          />
          <Input 
            placeholder="Reason for warning" 
            className="flex-1 min-w-[200px]"
            value={newWarning.reason}
            onChange={(e) => setNewWarning(prev => ({ ...prev, reason: e.target.value }))}
            data-testid="warning-reason-input"
          />
          <Button type="button" onClick={addWarningLetter} size="sm" variant="outline" data-testid="add-warning-btn">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>

        <div className="space-y-2 max-h-[200px] overflow-y-auto">
          {formData.warning_letters.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No warning letters recorded</p>
          ) : (
            formData.warning_letters.map((warning, index) => {
              const issueDate = new Date(warning.issue_date);
              const expiryDate = new Date(issueDate);
              expiryDate.setMonth(expiryDate.getMonth() + 6);
              const isActive = expiryDate > new Date();
              
              return (
                <div key={index} className={`flex items-center justify-between p-2 rounded border ${isActive ? 'bg-yellow-100 border-yellow-300' : 'bg-gray-100 border-gray-300'}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {getSeverityBadge(warning.severity)}
                      <span className="text-sm font-medium">
                        {new Date(warning.issue_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                      {isActive ? (
                        <span className="text-xs text-yellow-700">(Active until {expiryDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })})</span>
                      ) : (
                        <span className="text-xs text-gray-500">(Expired)</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-0.5">{warning.reason}</p>
                    {warning.issued_by && (
                      <p className="text-xs text-gray-500">Issued by: {warning.issued_by}</p>
                    )}
                  </div>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => removeWarningLetter(index)}
                    data-testid={`remove-warning-${index}-btn`}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default WarningLettersSection;
